import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useStore } from './lib/store'
import { LandingPage } from './pages/LandingPage'
import { FreedAlternativePage } from './pages/FreedAlternativePage'
import { LoginPage } from './pages/LoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { RegisterPage } from './pages/RegisterPage'
import { AuthCallbackPage } from './pages/AuthCallbackPage'
import { DashboardPage } from './pages/DashboardPage'
import { QuickstartPage, shouldShowQuickstart } from './pages/QuickstartPage'
import { WelcomeTourPage } from './pages/WelcomeTourPage'
import { NewEncounterPage } from './pages/NewEncounterPage'
import { EncounterPage } from './pages/EncounterPage'
import { NotePage } from './pages/NotePage'
import { PatientsPage } from './pages/PatientsPage'
import { ExtensionPage } from './pages/ExtensionPage'
import { PatientDetailPage } from './pages/PatientDetailPage'
import { TemplatesPage } from './pages/TemplatesPage'
import { SettingsPage } from './pages/SettingsPage'
import { SupportPage } from './pages/SupportPage'
import { LearningPage } from './pages/LearningPage'
import { IntakePage } from './pages/IntakePage'
import { StationLoginPage } from './pages/StationLoginPage'
import { StationApprovePage } from './pages/StationApprovePage'
import { IntakeManagementPage } from './pages/IntakeManagementPage'
import { AppShell } from './components/layout/AppShell'
import { SecurityPage } from './pages/SecurityPage'
import { TermsPage } from './pages/TermsPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { BaaPage } from './pages/BaaPage'
import { OAuthOnboardingPage } from './pages/OAuthOnboardingPage'

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, logout, doctor } = useStore()

  // Auto-logoff after 30 min inactivity (HIPAA)
  useEffect(() => {
    if (!isAuthenticated) return

    let timer: ReturnType<typeof setTimeout>
    const resetTimer = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        logout()
        window.location.href = '/login?reason=timeout'
      }, INACTIVITY_TIMEOUT_MS)
    }

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }))
    resetTimer()

    return () => {
      clearTimeout(timer)
      events.forEach(e => window.removeEventListener(e, resetTimer))
    }
  }, [isAuthenticated, logout])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-pulse text-brand-500 text-lg">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Remember where the user was headed (e.g. /settings from the extension
    // upgrade link) so login can return them there instead of the dashboard.
    try {
      const dest = window.location.pathname + window.location.search + window.location.hash
      if (dest && dest !== '/') sessionStorage.setItem('scribey_post_login', dest)
    } catch (e) { /* sessionStorage unavailable — non-fatal */ }
    return <Navigate to="/home" replace />
  }

  // Onboarding guard: OAuth users who haven't completed onboarding
  if (doctor && doctor.onboarding_complete === false && window.location.pathname !== '/onboarding') {
    return <Navigate to='/onboarding' replace />
  }

  return <>{children}</>
}

function HomeRoute() {
  // Evaluated on every render so the redirect clears as soon as markQuickstartSeen()
  // updates the store (prevents "Start my first visit" bouncing back to /welcome).
  const { doctor } = useStore()
  return shouldShowQuickstart(doctor) ? <Navigate to="/welcome" replace /> : <DashboardPage />
}

export default function App() {
  const { checkAuth } = useStore()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/station-login" element={<StationLoginPage />} />
      <Route path="/station-approve" element={<StationApprovePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Public routes - no auth required */}
      <Route path="/intake/:token" element={<IntakePage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/baa" element={<BaaPage />} />
      <Route path="/onboarding" element={<OAuthOnboardingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/freed-alternative" element={<FreedAlternativePage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/quickstart" element={<QuickstartPage />} />
                <Route path="/welcome" element={<WelcomeTourPage />} />
                <Route path="/new-encounter" element={<NewEncounterPage />} />
                <Route path="/encounter/:id" element={<EncounterPage />} />
                <Route path="/encounter/:id/note" element={<NotePage />} />
                <Route path="/encounter/:id/note/:noteId" element={<NotePage />} />
                <Route path="/patients" element={<PatientsPage />} />
                <Route path="/patients/:id" element={<PatientDetailPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/learning" element={<LearningPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/intake" element={<IntakeManagementPage />} />
                <Route path="/extension" element={<ExtensionPage />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
